import { BEAD_SPECS } from '../domain/beads';
import { ColorMatcher, parseHex } from '../domain/color';
import { buildLayout } from '../domain/stitches';
import { EMPTY, EMPTY_COLOR } from '../domain/types';
import type { BeadColor, BeadSizeId, Pattern, Rect, StitchId } from '../domain/types';
import { clampDim, createPattern } from '../domain/pattern';

export type DitherMode = 'none' | 'floyd' | 'atkinson' | 'ordered';

export interface ImageImportOptions {
  gridW: number;
  gridH: number;
  stitch: StitchId;
  beadSize: BeadSizeId;
  /** Bead colours the image is allowed to use. */
  candidates: BeadColor[];
  /** Cap the number of distinct beads; 0 means no cap. */
  maxColors: number;
  dither: DitherMode;
  /** -100..100 */
  brightness: number;
  contrast: number;
  saturation: number;
  /** Pixels with alpha below this become empty cells. */
  alphaThreshold: number;
  /** Source rectangle in image pixels; defaults to the whole image. */
  crop?: Rect;
  name?: string;
}

export function defaultImportOptions(candidates: BeadColor[]): ImageImportOptions {
  return {
    gridW: 40,
    gridH: 40,
    stitch: 'loom',
    beadSize: 'delica11',
    candidates,
    maxColors: 12,
    dither: 'none',
    brightness: 0,
    contrast: 0,
    saturation: 0,
    alphaThreshold: 24,
  };
}

export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Could not read ${file.name} as an image`));
    };
    img.src = url;
  });
}

/**
 * Grid height that keeps the picture's proportions.
 *
 * Bead cells are not square, so a 40x40 grid of Delicas is noticeably taller
 * than it is wide. This converts through the real millimetre sizes.
 */
export function proportionalHeight(
  imgW: number,
  imgH: number,
  gridW: number,
  stitch: StitchId,
  beadSize: BeadSizeId,
): number {
  const l = buildLayout(stitch, beadSize, 1, 1);
  const h = (gridW * l.cellW * imgH) / (l.cellH * imgW);
  return clampDim(Math.round(h));
}

export function proportionalWidth(
  imgW: number,
  imgH: number,
  gridH: number,
  stitch: StitchId,
  beadSize: BeadSizeId,
): number {
  const l = buildLayout(stitch, beadSize, 1, 1);
  const w = (gridH * l.cellH * imgW) / (l.cellW * imgH);
  return clampDim(Math.round(w));
}

interface Sampled {
  data: Float32Array; // rgba, straight alpha, 0..255
  w: number;
  h: number;
}

function sample(img: HTMLImageElement, opts: ImageImportOptions): Sampled {
  const w = clampDim(opts.gridW);
  const h = clampDim(opts.gridH);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  const crop = opts.crop ?? { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight };
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, w, h);
  const raw = ctx.getImageData(0, 0, w, h).data;

  const data = new Float32Array(w * h * 4);
  const bright = opts.brightness * 2.55;
  const contrast = (259 * (opts.contrast + 255)) / (255 * (259 - opts.contrast));
  const sat = 1 + opts.saturation / 100;

  for (let i = 0; i < w * h; i++) {
    let r = raw[i * 4];
    let g = raw[i * 4 + 1];
    let b = raw[i * 4 + 2];
    const a = raw[i * 4 + 3];

    r = contrast * (r - 128) + 128 + bright;
    g = contrast * (g - 128) + 128 + bright;
    b = contrast * (b - 128) + 128 + bright;

    if (sat !== 1) {
      const grey = 0.299 * r + 0.587 * g + 0.114 * b;
      r = grey + (r - grey) * sat;
      g = grey + (g - grey) * sat;
      b = grey + (b - grey) * sat;
    }

    data[i * 4] = r;
    data[i * 4 + 1] = g;
    data[i * 4 + 2] = b;
    data[i * 4 + 3] = a;
  }
  return { data, w, h };
}

const BAYER4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];

function quantize(
  src: Sampled,
  matcher: ColorMatcher,
  dither: DitherMode,
  alphaThreshold: number,
): Int32Array {
  const { w, h } = src;
  const buf = new Float32Array(src.data); // working copy, error diffuses into it
  const out = new Int32Array(w * h).fill(-1);
  const clamp = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);

  const push = (x: number, y: number, er: number, eg: number, eb: number, f: number) => {
    if (x < 0 || x >= w || y < 0 || y >= h) return;
    const i = (y * w + x) * 4;
    buf[i] += er * f;
    buf[i + 1] += eg * f;
    buf[i + 2] += eb * f;
  };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (buf[i + 3] < alphaThreshold) continue;

      let r = buf[i];
      let g = buf[i + 1];
      let b = buf[i + 2];

      if (dither === 'ordered') {
        // Nudge before matching; spreads flat areas across neighbouring beads.
        const t = (BAYER4[y & 3][x & 3] / 16 - 0.5) * 26;
        r += t;
        g += t;
        b += t;
      }

      const idx = matcher.nearest({ r: clamp(r), g: clamp(g), b: clamp(b) });
      out[y * w + x] = idx;

      if (dither === 'floyd' || dither === 'atkinson') {
        const chosen = matcher.rgbOf(idx);
        const er = r - chosen.r;
        const eg = g - chosen.g;
        const eb = b - chosen.b;
        if (dither === 'floyd') {
          push(x + 1, y, er, eg, eb, 7 / 16);
          push(x - 1, y + 1, er, eg, eb, 3 / 16);
          push(x, y + 1, er, eg, eb, 5 / 16);
          push(x + 1, y + 1, er, eg, eb, 1 / 16);
        } else {
          const f = 1 / 8;
          push(x + 1, y, er, eg, eb, f);
          push(x + 2, y, er, eg, eb, f);
          push(x - 1, y + 1, er, eg, eb, f);
          push(x, y + 1, er, eg, eb, f);
          push(x + 1, y + 1, er, eg, eb, f);
          push(x, y + 2, er, eg, eb, f);
        }
      }
    }
  }
  return out;
}

export interface ImportResult {
  pattern: Pattern;
  /** Colours that ended up unused after the colour cap was applied. */
  colorsUsed: number;
}

export function imageToPattern(img: HTMLImageElement, opts: ImageImportOptions): ImportResult {
  const src = sample(img, opts);
  let candidates = opts.candidates.filter((c) => c.id !== 'empty');
  if (candidates.length === 0) throw new Error('Pick at least one bead colour to match against');

  let matcher = new ColorMatcher(candidates.map((c) => c.hex));

  if (opts.maxColors > 0 && candidates.length > opts.maxColors) {
    // Choose which beads to buy from an undithered pass. Picking them from a
    // dithered one instead would count the scattered error pixels as if they
    // were real areas of colour and fill the palette with speckle.
    const flat = quantize(src, matcher, 'none', opts.alphaThreshold);
    const tally = new Map<number, number>();
    for (const v of flat) if (v >= 0) tally.set(v, (tally.get(v) ?? 0) + 1);
    const keep = [...tally.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, opts.maxColors)
      .map(([i]) => candidates[i]);
    if (keep.length > 0) {
      candidates = keep;
      matcher = new ColorMatcher(candidates.map((c) => c.hex));
    }
  }

  // Final pass dithers within the colours that were actually chosen.
  const mapped = quantize(src, matcher, opts.dither, opts.alphaThreshold);

  // Build the final palette from just the colours that survived.
  const used = new Map<number, number>();
  const palette: BeadColor[] = [EMPTY_COLOR];
  for (const v of mapped) {
    if (v < 0 || used.has(v)) continue;
    used.set(v, palette.length);
    palette.push(candidates[v]);
  }

  const pattern = createPattern({
    name: opts.name ?? 'Imported image',
    stitch: opts.stitch,
    beadSize: opts.beadSize,
    width: src.w,
    height: src.h,
  });
  pattern.palette = palette;
  for (let i = 0; i < mapped.length; i++) {
    const v = mapped[i];
    pattern.cells[i] = v < 0 ? EMPTY : used.get(v)!;
  }

  return { pattern, colorsUsed: palette.length - 1 };
}

/** A small preview of what the import will look like, for the dialog. */
export function renderImportPreview(
  ctx: CanvasRenderingContext2D,
  pattern: Pattern,
  boxW: number,
  boxH: number,
): void {
  const spec = BEAD_SPECS[pattern.beadSize];
  const l = buildLayout(pattern.stitch, pattern.beadSize, pattern.width, pattern.height);
  const scale = Math.min(boxW / l.totalW, boxH / l.totalH);
  const cw = l.cellW * scale;
  const chh = l.cellH * scale;
  const offX = (boxW - l.totalW * scale) / 2;
  const offY = (boxH - l.totalH * scale) / 2;
  ctx.clearRect(0, 0, boxW, boxH);
  for (let row = 0; row < pattern.height; row++) {
    for (let col = 0; col < pattern.width; col++) {
      const v = pattern.cells[row * pattern.width + col];
      if (v === EMPTY) continue;
      const p = l.pos(col, row);
      ctx.fillStyle = pattern.palette[v]?.hex ?? '#000';
      ctx.fillRect(offX + p.x * scale, offY + p.y * scale, Math.max(1, cw), Math.max(1, chh));
    }
  }
  void spec;
}

/** Turn an arbitrary colour into the nearest bead in a library. */
export function matchColor(hex: string, candidates: BeadColor[]): BeadColor | null {
  const list = candidates.filter((c) => c.id !== 'empty');
  if (list.length === 0) return null;
  const m = new ColorMatcher(list.map((c) => c.hex));
  return list[m.nearest(parseHex(hex))] ?? null;
}
